/**
 * JWT decoding utilities for extracting claims from Cognito tokens.
 * Uses base64 decoding only (no signature verification — that's the BFF's job).
 *
 * The frontend decodes the ID token to extract user metadata (role, groups)
 * for UI-level decisions like the admin route guard. Security enforcement
 * happens server-side in the BFF auth middleware.
 */

import { getTokens } from './auth-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UserRole = 'admin' | 'user';

export interface JwtPayload {
  readonly sub: string;
  readonly email?: string;
  readonly name?: string;
  readonly 'cognito:groups'?: string[];
  readonly 'custom:role'?: string;
  readonly 'custom:groups'?: string;
}

// ---------------------------------------------------------------------------
// Admin group list (mirrors BFF config)
// ---------------------------------------------------------------------------

const ADMIN_GROUPS: readonly string[] = ['admin', 'administrators'];

/** Dynamic admin group IDs fetched from BFF (e.g., Entra AD group object ID) */
let dynamicAdminGroups: string[] = [];

/**
 * Sets the dynamic admin group IDs fetched from the system settings.
 * Called once on app startup after fetching /v1/admin/settings.
 */
export function setDynamicAdminGroups(groups: string[]): void {
  dynamicAdminGroups = groups;
}

/**
 * Returns the full list of admin groups (static + dynamic).
 */
function getAllAdminGroups(): readonly string[] {
  return [...ADMIN_GROUPS, ...dynamicAdminGroups];
}

// ---------------------------------------------------------------------------
// JWT decode (payload only — no verification)
// ---------------------------------------------------------------------------

/**
 * Decodes a JWT token's payload without verification.
 * Returns null if the token format is invalid.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    if (!payload) return null;

    // Base64url to base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = atob(base64);
    return JSON.parse(jsonStr) as JwtPayload;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Role extraction
// ---------------------------------------------------------------------------

/**
 * Determines the user's role from the current ID token claims.
 * Mirrors the BFF logic: admin if in an admin group OR custom:role is 'admin'
 * OR custom:groups contains a configured admin group ID.
 */
export function getUserRole(): UserRole {
  const tokens = getTokens();
  if (!tokens) return 'user';

  const claims = decodeJwtPayload(tokens.idToken);
  if (!claims) return 'user';

  const groups = claims['cognito:groups'] ?? [];
  const adminGroups = getAllAdminGroups();
  const isInAdminGroup = groups.some((g) => adminGroups.includes(g));

  if (isInAdminGroup || claims['custom:role'] === 'admin') {
    return 'admin';
  }

  // Check custom:groups (IdP group claim, e.g. Entra ID group object IDs)
  // Must match the configured admin group ID — not just "any group"
  if (claims['custom:groups']) {
    try {
      const idpGroups: string[] = JSON.parse(claims['custom:groups']);
      // Check if any of the user's IdP groups match the known admin groups
      if (idpGroups.some((g) => adminGroups.includes(g))) {
        return 'admin';
      }
    } catch {
      // Not JSON — check single value
      if (adminGroups.includes(claims['custom:groups'])) {
        return 'admin';
      }
    }
  }

  return 'user';
}

/**
 * Returns true if the current user has admin privileges.
 */
export function isAdmin(): boolean {
  return getUserRole() === 'admin';
}
